import BaseComponent from 'scripts/BaseComponent.js';
import { AccessibilityHelper } from 'scripts/accessibility/accessibilityHelper';
import UnderlayHelper from '../../global/underlay-helper';

export default class NavigationDesktop extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      mainLinks: '.js-navigationMenuDesktopLink',
      drawer: '.js-navDrawer',
      navMenuLinkClass: '.js-navigationMenuDesktopLink',
      drawerSubMenuClass: '.js-navSubMenu',
      drawerSubMenuCloseBtn: '.js-navSubMenuClose',
      header: '.js-header',
    };

    this.dom = {
      $component: component,
      $mainLinks: this.$getAll(this.refs.mainLinks),
      $drawer: this.$get(this.refs.drawer),
      $drawerCloseBtns: this.$getAll(this.refs.drawerSubMenuCloseBtn),
      $header: document.querySelector(this.refs.header),
    };

    this.state = {
      drawerOpen: false,
      drawerOpenDelay: null,
      drawerHover: false,
      linkHover: false,
      activeDrawerNav: '',
      activeClass: 'is-active',
      inactiveClass: 'is-inactive',
      underlayHelper: new UnderlayHelper(),
      accessibilityHelper: new AccessibilityHelper(),
      focusTrap: null,
    };

    this.mount();
  }

  mount() {
    const { dom } = this;

    this.setOnEventWithElement('mouseenter', dom.$mainLinks, this.mainLinkEnter);
    this.setOnEvent('mouseleave', dom.$mainLinks, this.mainLinkLeave);
    this.setOnEvent('mouseenter', dom.$drawer, this.drawerEnter);
    this.setOnEvent('mouseleave', dom.$drawer, this.drawerLeave);
    this.setOnEvent('keydown', dom.$mainLinks, this.mainLinkKeyDown, {
      preventDefault: false,
    });
    this.setClick(dom.$drawerCloseBtns, this.keyboardCloseDrawer);
    this.setOnEscapeKey(this.escapePress);
  }

  mainLinkEnter(link) {
    const { state } = this;
    state.linkHover = true;
    const subNavUrl = link.dataset.sublinks;

    this.unsetLinkClasses();
    this.setLinkClasses(link);

    if (!subNavUrl) {
      this.closeDrawer();
      return;
    }

    if (state.drawerOpenDelay) {
      clearTimeout(state.drawerOpenDelay);
      state.drawerOpenDelay = null;
    }

    this.clearSubNav();

    if (!state.drawerOpen) {
      state.drawerOpenDelay = setTimeout(async () => {
        this.openDrawer();

        if (
          subNavUrl === state.activeDrawerNav
            || state.activeDrawerNav === ''
        ) {
          this.setSubNav(subNavUrl);
        }

        state.drawerOpenDelay = null;
      }, 250);
      return;
    }

    this.setSubNav(subNavUrl);
  }

  async mainLinkLeave() {
    const { state } = this;
    state.linkHover = false;

    await new Promise((resolve) => { setTimeout(resolve, 0); });
    if (state.drawerHover || state.linkHover) return;

    if (state.drawerOpenDelay) {
      clearTimeout(state.drawerOpenDelay);
      state.drawerOpenDelay = null;
    }

    this.unsetLinkClasses();
    this.closeDrawer();
    if (state.focusTrap) {
      state.focusTrap.unset();
      state.focusTrap = null;
    }
  }

  mainLinkKeyDown(event) {
    const { state } = this;
    const $link = event.target;
    const subNavUrl = $link.dataset.sublinks;
    if (event.key === 'Enter') {
      if (!subNavUrl) {
        const { href } = $link.querySelector('a');
        window.location.href = href;
      } else if (!state.drawerOpen) {
        this.mainLinkEnter(event.target);
        const $activeDrawer = this.getActiveDrawer(subNavUrl);
        state.focusTrap = state.accessibilityHelper.buildFocusTrap([$activeDrawer]);
        state.focusTrap.set();
        setTimeout(() => {
          state.focusTrap.focusOnFirstElement();
        }, 300);
      } else {
        this.mainLinkLeave();
      }
    }
  }

  keyboardCloseDrawer() {
    const $activeLink = this.getActiveLink();
    $activeLink.focus();
    this.mainLinkLeave();
  }

  setLinkClasses($activeLink) {
    const { state } = this;
    $activeLink.classList.add(state.activeClass);
    const $inactiveLinks = this.getInactiveLinks();
    $inactiveLinks.forEach(($link) => {
      $link.classList.add(state.inactiveClass);
    });
  }

  unsetLinkClasses() {
    const { state } = this;
    const $activeLink = this.getActiveLink();
    if ($activeLink) {
      $activeLink.classList.remove(state.activeClass);
    }
    const $inactiveLinks = this.getInactiveLinks();
    $inactiveLinks.forEach(($link) => {
      $link.classList.remove(state.inactiveClass);
    });
  }

  setSubNav(url) {
    const { state } = this;
    if (!url) {
      state.activeDrawerNav = '';
      return;
    }
    const $activeDrawer = this.getActiveDrawer(url);
    $activeDrawer.classList.add(state.activeClass);
    state.activeDrawerNav = url;
  }

  clearSubNav() {
    const { state } = this;
    if (!state.activeDrawerNav) return;
    const $activeDrawer = this.getActiveDrawer(state.activeDrawerNav);
    $activeDrawer.classList.remove(state.activeClass);
    state.activeDrawerNav = '';
  }

  drawerEnter() {
    const { state } = this;
    state.drawerHover = true;
  }

  async drawerLeave() {
    const { state } = this;
    state.drawerHover = false;

    await new Promise((resolve) => { setTimeout(resolve, 0); });
    if (state.linkHover) return;

    this.closeDrawer();
    this.unsetLinkClasses();
  }

  openDrawer() {
    const { dom, state } = this;
    dom.$drawer.classList.add(state.activeClass);
    dom.$header.classList.add(state.activeClass);
    state.underlayHelper.open();
    state.drawerOpen = true;
  }

  closeDrawer() {
    const { dom, state } = this;
    dom.$drawer.classList.remove(state.activeClass);
    dom.$header.classList.remove(state.activeClass);
    state.underlayHelper.close();
    state.drawerOpen = false;
    this.clearSubNav();
  }

  escapePress() {
    this.mainLinkLeave();
  }

  getActiveLink() {
    const { dom, state, refs } = this;
    const activeLinkSelector = `${refs.navMenuLinkClass}.${state.activeClass}`;
    return dom.$component.querySelector(activeLinkSelector);
  }

  getInactiveLinks() {
    const { dom, state, refs } = this;
    return dom.$component.querySelectorAll(`${refs.navMenuLinkClass}:not(.${state.activeClass})`);
  }

  getActiveDrawer(url) {
    const { dom, refs } = this;
    return dom.$component.querySelector(`${refs.drawerSubMenuClass}[data-sublinks="${url}"]`);
  }
}
